import React, {useEffect, useRef, useState} from 'react';
import {ChevronDownIcon, GlobeAltIcon} from '@heroicons/react/24/outline';
import {useClustersMarket} from '../contexts/ClustersMarketContext';
import {CAPTIONED_TRIGGER_RESTING_CLASSES, CaptionLabel} from '../../../components/CaptionedField';

interface MarketSelectorProps {
    /**
     * Opt-in "Market" caption + accent-bordered trigger treatment
     * (deepcontext-mfoi) — the same chrome AnalysisProfileSelect's
     * `captioned` prop gives Blueprint on Media Planning. Default false
     * keeps the plain header-row look untouched for any caller that hasn't
     * moved to the captioned left-of-header placement yet.
     */
    captioned?: boolean;
}

/**
 * Top-bar market selector (deepcontext-d68.12).
 *
 * Replaces the source repo's static "TR" badge. Reads markets + selection
 * from ClustersMarketContext. Renders as a labelled button that toggles a
 * dropdown listing discovered countries (UPPERCASE ISO-3166) with their URL counts.
 */
const MarketSelector: React.FC<MarketSelectorProps> = ({captioned = false}) => {
    const {markets, selectedMarket, setSelectedMarket, loading, error} = useClustersMarket();
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [open]);

    const current = markets.find((m) => m.code === selectedMarket) ?? null;
    const buttonLabel = current ? current.label : loading ? 'Loading…' : 'Select market';

    const handleSelect = (code: string) => {
        setSelectedMarket(code);
        setOpen(false);
    };

    return (
        <div className="relative inline-block" ref={containerRef} data-testid="market-selector">
            {captioned && <CaptionLabel label="Market" />}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                disabled={loading && markets.length === 0}
                aria-haspopup="listbox"
                aria-expanded={open}
                className={`inline-flex items-center gap-1.5 rounded-full h-[42px] px-3 text-ink hover:bg-ghost-bg focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-60 ${
                    captioned
                        ? `text-sm font-bold ${CAPTIONED_TRIGGER_RESTING_CLASSES}`
                        : 'border border-line bg-surface text-xs font-medium'
                }`}
                data-testid="market-selector-button"
            >
                <GlobeAltIcon className="h-3.5 w-3.5" />
                <span>{buttonLabel}</span>
                {current && (
                    <span className="text-ink-muted">({current.code.toUpperCase()})</span>
                )}
                <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div
                    role="listbox"
                    aria-label="Select market"
                    className="absolute left-0 z-20 mt-1 max-h-72 w-60 overflow-y-auto rounded-md border border-line bg-surface shadow-lg"
                    data-testid="market-selector-menu"
                >
                    {error && (
                        <div className="px-3 py-2 text-xs text-risk" role="alert">
                            {error}
                        </div>
                    )}
                    {!error && markets.length === 0 && (
                        <div className="px-3 py-2 text-xs text-ink-muted">
                            {loading ? 'Loading markets…' : 'No markets available.'}
                        </div>
                    )}
                    {markets.map((m) => {
                        const isSelected = m.code === selectedMarket;
                        return (
                            <button
                                type="button"
                                key={m.code}
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => handleSelect(m.code)}
                                className={`flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-xs hover:bg-ghost-bg ${
                                    isSelected ? 'bg-ghost-bg text-accent font-medium' : 'text-ink'
                                }`}
                                data-testid={`market-selector-option-${m.code}`}
                            >
                                <span className="truncate">
                                    {m.label} <span className="text-ink-muted">({m.code})</span>
                                </span>
                                <span className="shrink-0 text-ink-muted">
                                    {m.urlCount.toLocaleString()}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MarketSelector;
